import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Spinner, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faUpload } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { uploadRiversamentiImage, uploadRiversamentiVideo } from '../../services/api';

export const PAGINE = [
    { valore: 'home', etichetta: 'Home' },
    { valore: 'vhs', etichetta: 'VHS e videocassette' },
    { valore: 'super8', etichetta: 'Super8 e 8mm' },
    { valore: 'audio', etichetta: 'Audio e vinili' },
    { valore: 'diapositive', etichetta: 'Diapositive' },
    { valore: 'servizi', etichetta: 'Servizi' },
    { valore: 'prezzi', etichetta: 'Prezzi' },
    { valore: 'come-funziona', etichetta: 'Come funziona' },
    { valore: 'preventivo', etichetta: 'Preventivo' },
    { valore: 'preventivo-inviato', etichetta: 'Preventivo · Richiesta ricevuta' },
];

export const TIPI = [
    { valore: 'intestazione', etichetta: 'Testata (titolo, sottotitolo e foto grande)' },
    { valore: 'testo', etichetta: 'Solo testo' },
    { valore: 'immagine', etichetta: 'Solo foto' },
    { valore: 'testo_immagine', etichetta: 'Testo con foto affiancata' },
    { valore: 'galleria', etichetta: 'Galleria di foto e video' },
    { valore: 'faq', etichetta: 'Domande frequenti' },
];

// Il backend restituisce /uploads/...: lo StaticFiles e' montato su /api/uploads
export function urlAnteprima(imageUrl) {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('http')) return imageUrl;
    const base = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    return imageUrl.startsWith('/api/') ? `${base}${imageUrl}` : `${base}/api${imageUrl}`;
}

const vuoto = {
    pagina: 'home',
    zona: 'basso',
    block_type: 'testo',
    title: '',
    is_active: true,
    order: 0,
    content: {},
};

const CampoFoto = ({ label, value, onChange, aiuto }) => {
    const [caricamento, setCaricamento] = useState(false);

    const carica = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setCaricamento(true);
        try {
            const res = await uploadRiversamentiImage(file);
            onChange(res.data.image_url);
            toast.success('Foto caricata.');
        } catch (err) {
            toast.error("Errore nel caricamento della foto.");
        } finally {
            setCaricamento(false);
            e.target.value = '';
        }
    };

    return (
        <Form.Group className="mb-3">
            <Form.Label>{label}</Form.Label>
            <div className="d-flex align-items-start gap-3">
                <div
                    className="border rounded d-flex align-items-center justify-content-center bg-light"
                    style={{ width: 160, height: 120, overflow: 'hidden', flexShrink: 0 }}
                >
                    {value ? (
                        <img
                            src={urlAnteprima(value)}
                            alt=""
                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                    ) : (
                        <span className="text-muted small">Nessuna foto</span>
                    )}
                </div>
                <div className="flex-grow-1">
                    <Form.Control type="file" accept="image/*" onChange={carica} disabled={caricamento} />
                    {caricamento && <Spinner animation="border" size="sm" className="mt-2" />}
                    {aiuto && <Form.Text className="d-block">{aiuto}</Form.Text>}
                    {value && (
                        <Button variant="link" size="sm" className="px-0 text-danger" onClick={() => onChange('')}>
                            Togli la foto
                        </Button>
                    )}
                </div>
            </div>
        </Form.Group>
    );
};

const CampoVideo = ({ label, value, onChange, aiuto }) => {
    const [caricamento, setCaricamento] = useState(false);

    const carica = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setCaricamento(true);
        try {
            const res = await uploadRiversamentiVideo(file);
            onChange(res.data.video_url);
            toast.success('Video caricato.');
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore nel caricamento del video.");
        } finally {
            setCaricamento(false);
            e.target.value = '';
        }
    };

    return (
        <Form.Group className="mb-3">
            <Form.Label>{label}</Form.Label>
            <div className="d-flex align-items-start gap-3">
                <div
                    className="border rounded d-flex align-items-center justify-content-center bg-dark"
                    style={{ width: 160, height: 120, overflow: 'hidden', flexShrink: 0 }}
                >
                    {value ? (
                        <video src={urlAnteprima(value)} style={{ maxWidth: '100%', maxHeight: '100%' }} muted />
                    ) : (
                        <span className="text-muted small">Nessun video</span>
                    )}
                </div>
                <div className="flex-grow-1">
                    <Form.Control type="file" accept="video/mp4,video/webm" onChange={carica} disabled={caricamento} />
                    {caricamento && <Spinner animation="border" size="sm" className="mt-2" />}
                    <Form.Text className="d-block">{aiuto || 'Solo mp4 o webm, massimo 50 MB.'}</Form.Text>
                    {value && (
                        <Button variant="link" size="sm" className="px-0 text-danger" onClick={() => onChange('')}>
                            Togli il video
                        </Button>
                    )}
                </div>
            </div>
        </Form.Group>
    );
};

const RiversamentiBlockForm = ({ show, onHide, onSubmit, initialData, paginaCorrente }) => {
    const [dati, setDati] = useState(vuoto);
    const [salvataggio, setSalvataggio] = useState(false);
    const isNuovo = !initialData;

    useEffect(() => {
        if (initialData) {
            setDati({ ...initialData, content: initialData.content || {} });
        } else {
            setDati({ ...vuoto, pagina: paginaCorrente || 'home', content: {} });
        }
    }, [initialData, paginaCorrente, show]);

    const cambia = (campo, valore) => setDati((p) => ({ ...p, [campo]: valore }));
    const cambiaContenuto = (campo, valore) =>
        setDati((p) => ({ ...p, content: { ...p.content, [campo]: valore } }));

    const voci = Array.isArray(dati.content?.voci) ? dati.content.voci : [];
    const immagini = Array.isArray(dati.content?.immagini) ? dati.content.immagini : [];

    const cambiaVoce = (i, campo, valore) => {
        const nuove = voci.map((v, idx) => (idx === i ? { ...v, [campo]: valore } : v));
        cambiaContenuto('voci', nuove);
    };
    const cambiaImmagine = (i, campo, valore) => {
        const nuove = immagini.map((im, idx) => (idx === i ? { ...im, [campo]: valore } : im));
        cambiaContenuto('immagini', nuove);
    };

    const salva = async (e) => {
        e.preventDefault();
        setSalvataggio(true);
        try {
            await onSubmit(dati);
        } finally {
            setSalvataggio(false);
        }
    };

    const campiTipo = () => {
        switch (dati.block_type) {
            case 'intestazione':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Occhiello</Form.Label>
                            <Form.Control
                                value={dati.content.occhiello || ''}
                                onChange={(e) => cambiaContenuto('occhiello', e.target.value)}
                                placeholder="La riga piccola sopra il titolo"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Titolo principale (H1)</Form.Label>
                            <Form.Control
                                value={dati.content.h1 || ''}
                                onChange={(e) => cambiaContenuto('h1', e.target.value)}
                            />
                            <Form.Text>
                                È il titolo che legge Google. Lascia vuoto per tenere quello attuale.
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Testo introduttivo</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={dati.content.intro || ''}
                                onChange={(e) => cambiaContenuto('intro', e.target.value)}
                            />
                        </Form.Group>
                        <CampoFoto
                            label="Foto di testata"
                            value={dati.content.image_url}
                            onChange={(v) => cambiaContenuto('image_url', v)}
                            aiuto="Orizzontale, almeno 1200px di larghezza."
                        />
                        <Form.Group className="mb-3">
                            <Form.Label>Descrizione della foto</Form.Label>
                            <Form.Control
                                value={dati.content.image_alt || ''}
                                onChange={(e) => cambiaContenuto('image_alt', e.target.value)}
                                placeholder="Cosa si vede nella foto, per chi non la può vedere"
                            />
                        </Form.Group>
                    </>
                );

            case 'testo':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Titolo della sezione</Form.Label>
                            <Form.Control
                                value={dati.content.titolo || ''}
                                onChange={(e) => cambiaContenuto('titolo', e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Testo</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={7}
                                value={dati.content.testo || ''}
                                onChange={(e) => cambiaContenuto('testo', e.target.value)}
                            />
                            <Form.Text>Lascia una riga vuota per andare a capo con un paragrafo nuovo.</Form.Text>
                        </Form.Group>
                    </>
                );

            case 'immagine':
                return (
                    <>
                        <CampoFoto
                            label="Foto"
                            value={dati.content.image_url}
                            onChange={(v) => cambiaContenuto('image_url', v)}
                        />
                        <Form.Group className="mb-3">
                            <Form.Label>Descrizione della foto</Form.Label>
                            <Form.Control
                                value={dati.content.alt || ''}
                                onChange={(e) => cambiaContenuto('alt', e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Didascalia (facoltativa)</Form.Label>
                            <Form.Control
                                value={dati.content.didascalia || ''}
                                onChange={(e) => cambiaContenuto('didascalia', e.target.value)}
                            />
                        </Form.Group>
                    </>
                );

            case 'testo_immagine':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Titolo della sezione</Form.Label>
                            <Form.Control
                                value={dati.content.titolo || ''}
                                onChange={(e) => cambiaContenuto('titolo', e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Testo</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={6}
                                value={dati.content.testo || ''}
                                onChange={(e) => cambiaContenuto('testo', e.target.value)}
                            />
                            <Form.Text>Lascia una riga vuota per andare a capo con un paragrafo nuovo.</Form.Text>
                        </Form.Group>
                        <CampoFoto
                            label="Foto"
                            value={dati.content.image_url}
                            onChange={(v) => cambiaContenuto('image_url', v)}
                        />
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Descrizione della foto</Form.Label>
                                    <Form.Control
                                        value={dati.content.alt || ''}
                                        onChange={(e) => cambiaContenuto('alt', e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>La foto sta a</Form.Label>
                                    <Form.Select
                                        value={dati.content.posizione || 'destra'}
                                        onChange={(e) => cambiaContenuto('posizione', e.target.value)}
                                    >
                                        <option value="destra">Destra</option>
                                        <option value="sinistra">Sinistra</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                    </>
                );

            case 'galleria':
                return (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>Titolo della galleria (facoltativo)</Form.Label>
                            <Form.Control
                                value={dati.content.titolo || ''}
                                onChange={(e) => cambiaContenuto('titolo', e.target.value)}
                            />
                        </Form.Group>
                        {immagini.map((im, i) => {
                            const tipo = im.tipo === 'video' ? 'video' : 'immagine';
                            return (
                                <Card key={i} className="mb-3">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <strong>Elemento {i + 1}</strong>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() =>
                                                    cambiaContenuto('immagini', immagini.filter((_, idx) => idx !== i))
                                                }
                                            >
                                                <FontAwesomeIcon icon={faTrash} /> Togli
                                            </Button>
                                        </div>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Tipo</Form.Label>
                                            <Form.Select
                                                value={tipo}
                                                onChange={(e) => cambiaImmagine(i, 'tipo', e.target.value)}
                                            >
                                                <option value="immagine">Foto</option>
                                                <option value="video">Video</option>
                                            </Form.Select>
                                        </Form.Group>
                                        {tipo === 'video' ? (
                                            <CampoVideo
                                                label="Video"
                                                value={im.video_url}
                                                onChange={(v) => cambiaImmagine(i, 'video_url', v)}
                                            />
                                        ) : (
                                            <CampoFoto
                                                label="Immagine"
                                                value={im.image_url}
                                                onChange={(v) => cambiaImmagine(i, 'image_url', v)}
                                            />
                                        )}
                                        <Row>
                                            {tipo === 'immagine' && (
                                                <Col md={6}>
                                                    <Form.Control
                                                        className="mb-2"
                                                        placeholder="Descrizione della foto"
                                                        value={im.alt || ''}
                                                        onChange={(e) => cambiaImmagine(i, 'alt', e.target.value)}
                                                    />
                                                </Col>
                                            )}
                                            <Col md={tipo === 'immagine' ? 6 : 12}>
                                                <Form.Control
                                                    className="mb-2"
                                                    placeholder="Didascalia (facoltativa)"
                                                    value={im.didascalia || ''}
                                                    onChange={(e) => cambiaImmagine(i, 'didascalia', e.target.value)}
                                                />
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            );
                        })}
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => cambiaContenuto('immagini', [...immagini, { tipo: 'immagine', image_url: '', alt: '' }])}
                        >
                            <FontAwesomeIcon icon={faUpload} /> Aggiungi foto o video
                        </Button>
                    </>
                );

            case 'faq':
                return (
                    <>
                        <p className="text-muted">
                            Sostituiscono le domande frequenti già presenti sulla pagina. Finché questo
                            blocco non è attivo, restano quelle attuali.
                        </p>
                        {voci.map((v, i) => (
                            <Card key={i} className="mb-3">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <strong>Domanda {i + 1}</strong>
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => cambiaContenuto('voci', voci.filter((_, idx) => idx !== i))}
                                        >
                                            <FontAwesomeIcon icon={faTrash} /> Togli
                                        </Button>
                                    </div>
                                    <Form.Control
                                        className="mb-2"
                                        placeholder="Domanda"
                                        value={v.domanda || ''}
                                        onChange={(e) => cambiaVoce(i, 'domanda', e.target.value)}
                                    />
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="Risposta"
                                        value={v.risposta || ''}
                                        onChange={(e) => cambiaVoce(i, 'risposta', e.target.value)}
                                    />
                                </Card.Body>
                            </Card>
                        ))}
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => cambiaContenuto('voci', [...voci, { domanda: '', risposta: '' }])}
                        >
                            <FontAwesomeIcon icon={faPlus} /> Aggiungi una domanda
                        </Button>
                    </>
                );

            default:
                return null;
        }
    };

    // Testata e FAQ sostituiscono parti fisse della pagina: la zona non le riguarda
    const zonaRilevante = !['intestazione', 'faq'].includes(dati.block_type);

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Form onSubmit={salva}>
                <Modal.Header closeButton>
                    <Modal.Title>{isNuovo ? 'Nuovo blocco' : 'Modifica blocco'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Pagina</Form.Label>
                                <Form.Select value={dati.pagina} onChange={(e) => cambia('pagina', e.target.value)}>
                                    {PAGINE.map((p) => (
                                        <option key={p.valore} value={p.valore}>{p.etichetta}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Tipo di blocco</Form.Label>
                                <Form.Select
                                    value={dati.block_type}
                                    onChange={(e) => cambia('block_type', e.target.value)}
                                >
                                    {TIPI.map((t) => (
                                        <option key={t.valore} value={t.valore}>{t.etichetta}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label>Nome interno</Form.Label>
                                <Form.Control
                                    value={dati.title || ''}
                                    onChange={(e) => cambia('title', e.target.value)}
                                    placeholder="Serve solo a te per riconoscerlo in elenco"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            {zonaRilevante && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Dove sulla pagina</Form.Label>
                                    <Form.Select value={dati.zona} onChange={(e) => cambia('zona', e.target.value)}>
                                        <option value="alto">In alto, subito sotto la testata</option>
                                        <option value="basso">In basso, prima delle domande frequenti</option>
                                    </Form.Select>
                                </Form.Group>
                            )}
                        </Col>
                    </Row>
                    <Form.Check
                        type="switch"
                        id="blocco-attivo"
                        className="mb-3"
                        label="Visibile sul sito"
                        checked={!!dati.is_active}
                        onChange={(e) => cambia('is_active', e.target.checked)}
                    />
                    <hr />
                    {campiTipo()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>Annulla</Button>
                    <Button type="submit" variant="primary" disabled={salvataggio}>
                        {salvataggio ? <Spinner animation="border" size="sm" /> : 'Salva'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default RiversamentiBlockForm;
